import Phaser from "phaser";
import { FONT_FAMILY, TYPO } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { DEPTH } from "../objects/depthLayers.js";
import { t } from "../i18n/i18n.js";

const BAR_HEIGHT = 12;
/** 남은시간 비율이 이 값 이하면 fill을 위험색(빨강)으로. */
const DANGER_RATIO = 0.3;
const COLOR_NORMAL = PALETTE_NUM.infoBlue;
const COLOR_DANGER = PALETTE_NUM.signalDanger;

/**
 * 던전 전투 제한시간 표시 — 게임뷰 상단 오버레이 progress bar + 남은시간 텍스트.
 * 던전 전투(dungeonMode) 중에만 표시. GameScene.update()가 매 프레임 update(remaining,total) 호출.
 * 순수 시각 컴포넌트(Rectangle bg+fill + Text) — 남은시간 비율로 fill 너비, 임계 이하면 위험색.
 */
export class DungeonTimerBar {
  private readonly bg: Phaser.GameObjects.Rectangle;
  private readonly fill: Phaser.GameObjects.Rectangle;
  private readonly label: Phaser.GameObjects.Text;
  private readonly fullWidth: number;

  constructor(scene: Phaser.Scene, x: number, y: number, width: number) {
    this.fullWidth = width;
    this.bg = scene.add
      .rectangle(x, y, width, BAR_HEIGHT, PALETTE_NUM.cellBg, 0.85)
      .setOrigin(0, 0)
      .setDepth(DEPTH.dungeonTimerBg)
      .setVisible(false)
      .setActive(false);
    this.fill = scene.add
      .rectangle(x, y, width, BAR_HEIGHT, COLOR_NORMAL, 1)
      .setOrigin(0, 0)
      .setDepth(DEPTH.dungeonTimerFill)
      .setVisible(false)
      .setActive(false);
    this.label = scene.add
      .text(x + width / 2, y + BAR_HEIGHT / 2, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textBright,
      })
      .setOrigin(0.5)
      .setDepth(DEPTH.dungeonTimerLabel)
      .setVisible(false)
      .setActive(false);
  }

  /** 남은시간(ms)/총시간(ms) → fill 너비 비율 + 텍스트 갱신. */
  update(remainingMs: number, totalMs: number): void {
    const ratio = Phaser.Math.Clamp(totalMs > 0 ? remainingMs / totalMs : 0, 0, 1);
    this.fill.setSize(this.fullWidth * ratio, BAR_HEIGHT);
    this.fill.setFillStyle(ratio > DANGER_RATIO ? COLOR_NORMAL : COLOR_DANGER);
    const sec = Math.max(0, remainingMs / 1000);
    this.label.setText(t("dungeon.battleTimer", { sec: sec.toFixed(1) }));
  }

  setVisible(visible: boolean): void {
    this.bg.setVisible(visible).setActive(visible);
    this.fill.setVisible(visible).setActive(visible);
    this.label.setVisible(visible).setActive(visible);
  }

  /** 언어 변경 — 텍스트는 update()에서 매 프레임 t()로 갱신되므로 별도 처리 불필요(인터페이스 일관성용). */
  applyLanguage(): void {
    // no-op: 동적 라벨은 update()가 담당.
  }
}
